import { Canvas, FabricImage } from 'fabric';
import { useEffect, useRef } from 'react';

const addOverlayImage = (
  canvas: Canvas,
  src: string,
  designWidth: number,
  designHeight: number,
  onCreated: (img: FabricImage) => void
): (() => void) => {
  let cancelled = false;
  const img = new Image();

  img.onload = () => {
    if (cancelled) return;
    const fabricImg = new FabricImage(img, {
      left: designWidth / 2,
      top: designHeight / 2,
      originX: 'center',
      originY: 'center',
      scaleX: designWidth / img.naturalWidth,
      scaleY: designHeight / img.naturalHeight,
      selectable: false,
      evented: false,
      opacity: 1,
      excludeFromExport: true
    });
    fabricImg.isOverlay = true;
    canvas.add(fabricImg);
    canvas.bringObjectToFront(fabricImg);
    canvas.requestRenderAll();
    onCreated(fabricImg);
  };

  img.src = src;
  return () => {
    cancelled = true;
  };
};

export const useCanvasOverlay = (
  canvas: Canvas | null,
  src: string | undefined,
  designWidth: number,
  designHeight: number,
  show: boolean,
  layoutVersion: number
) => {
  const imgRef = useRef<FabricImage | null>(null);

  useEffect(() => {
    if (!canvas || !src) return;

    // canvas was cleared on project load — ref is stale
    imgRef.current = null;

    if (!show) return;

    let cancelAdd: () => void = () => {};

    const addOverlay = () => {
      cancelAdd();
      cancelAdd = addOverlayImage(canvas, src, designWidth, designHeight, img => {
        imgRef.current = img;
      });
    };

    // Re-add the overlay whenever the canvas is cleared (e.g. after undo/redo's loadFromJSON).
    const handleCleared = () => {
      imgRef.current = null;
      addOverlay();
    };

    addOverlay();
    canvas.on('canvas:cleared', handleCleared);

    return () => {
      canvas.off('canvas:cleared', handleCleared);
      cancelAdd();
      if (imgRef.current) {
        canvas.remove(imgRef.current);
        imgRef.current = null;
        canvas.requestRenderAll();
      }
    };
  }, [show, canvas, src, designWidth, designHeight, layoutVersion]);
};
